// A trilha de etapas do negócio — o "Path" do Salesforce, a barra de etapas do
// Pipedrive: uma faixa de setas, a etapa atual em destaque dizendo HÁ QUANTO
// TEMPO o negócio está nela. Clicar numa etapa move o negócio.
//
// Substitui o <Steps> do antd (01/10/2026). Três diferenças que importam:
//  • o tempo na etapa aparece (lido do histórico `status_alterado`, que já
//    existia e ninguém mostrava);
//  • clicar em "Fechamento" abre o fluxo de GANHAR (que gera a venda) em vez de
//    tentar gravar o status direto — o servidor recusava, e a tela dava erro;
//  • negócio perdido ou cancelado mostra a trilha apagada e o motivo ao lado.
import { useEffect, useRef } from 'react';
import { Tooltip } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { STATUS_LABELS, STATUS_ETAPAS } from './utils';

const CSS = `
.te-trilha { display: flex; min-width: 0; overflow-x: auto; }
.te-passo { position: relative; flex: 1 1 0; min-width: 0; border: 0; font: inherit; cursor: pointer; text-align: center;
  padding: 7px 18px 7px 24px; margin-left: -8px; background: #EEF1F8; color: #5A6378;
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%);
  transition: background .12s; }
.te-passo:first-child { margin-left: 0; padding-left: 14px; border-radius: 6px 0 0 6px;
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%); }
.te-passo:last-child { border-radius: 0 6px 6px 0;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%); }
.te-passo:hover:not(:disabled) { background: #E1E7F5; }
.te-passo:focus-visible { outline: 2px solid #0930D0; outline-offset: -2px; }
.te-passo:disabled { cursor: default; }
.te-passo.te-feito { background: #DCE5FB; color: #020D7F; }
.te-passo.te-atual { background: #0930D0; color: #fff; }
.te-passo.te-ganho { background: #0a7f4a; color: #fff; }
.te-passo.te-morto { background: #F1F2F5; color: #9AA0B4; }
.te-nome { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
.te-tempo { font-size: 10.5px; opacity: .85; display: block; white-space: nowrap; }
.te-compacta .te-passo { padding: 5px 8px 5px 15px; margin-left: -7px; }
.te-compacta .te-passo:first-child { padding-left: 8px; margin-left: 0; }
.te-compacta .te-nome { font-size: 10.5px; }
/* Tela estreita: seis etapas não cabem lado a lado. Cada uma fica do tamanho
   do próprio nome e a faixa rola de lado (o componente centraliza a atual). */
@media (max-width: 768px) {
  .te-passo { flex: 1 0 auto; }
  .te-nome { overflow: visible; }
}
`;

// Na espiada (painel de 520px) seis nomes inteiros não cabem: todas aparecem
// abreviadas e sem o "✓" (a cor já diz o que passou). O nome completo fica na
// dica, e o da etapa atual está escrito logo acima da trilha, na espiada.
const ROTULO_CURTO: Record<string, string> = {
  prospeccao: '1º contato', qualificacao: 'Necessid.', proposta: 'Proposta',
  follow_up: 'Follow-up', negociacao: 'Negoc.', ganho: 'Fechar',
};

export function TrilhaEtapas({ status, diasNaEtapa, onMudar, onGanhar, compacta = false, travadaPorque }: {
  status: string;
  /** Dias desde que o negócio entrou na etapa atual; null se não se sabe. */
  diasNaEtapa: number | null;
  /** Sem eles, a trilha é só de leitura (sem permissão, ou gêmeo da licitação). */
  onMudar?: (novo: string) => void;
  onGanhar?: () => void;
  compacta?: boolean;
  /** Por que a trilha está travada (vai na dica de cada etapa). */
  travadaPorque?: string;
}) {
  const faixa = useRef<HTMLDivElement>(null);
  // Quando a faixa rola de lado (celular), a etapa atual começa à vista.
  useEffect(() => {
    const c = faixa.current;
    const el = c?.querySelector<HTMLElement>('.te-atual');
    if (c && el && c.scrollWidth > c.clientWidth) {
      c.scrollLeft = Math.max(0, el.offsetLeft - (c.clientWidth - el.offsetWidth) / 2);
    }
  }, [status]);
  const idxAtual = STATUS_ETAPAS.indexOf(status);
  const morto = status === 'perdido' || status === 'cancelado';
  const ganho = status === 'ganho';
  const tempo = diasNaEtapa == null ? ''
    : diasNaEtapa === 0 ? 'desde hoje' : diasNaEtapa === 1 ? 'há 1 dia' : `há ${diasNaEtapa} dias`;

  return (
    <>
      <style>{CSS}</style>
      <div ref={faixa} className={'te-trilha' + (compacta ? ' te-compacta' : '')} role="group" aria-label="Etapas do negócio">
        {STATUS_ETAPAS.map((etapa, i) => {
          const atual = !morto && i === idxAtual;
          const feito = !morto && idxAtual >= 0 && i < idxAtual;
          const rotulo = STATUS_LABELS[etapa] || etapa;
          const classe = 'te-passo'
            + (morto ? ' te-morto' : ganho ? ' te-ganho' : atual ? ' te-atual' : feito ? ' te-feito' : '');
          const acao = etapa === 'ganho' ? onGanhar : onMudar && (() => onMudar(etapa));
          const dica = atual ? `${rotulo} — etapa atual${tempo ? ', ' + tempo : ''}`
            : ganho ? 'Negócio ganho não volta de etapa por aqui. Use "Desmarcar Ganho".'
            : !acao ? (travadaPorque || 'Sem permissão para mudar a etapa')
            : etapa === 'ganho' ? 'Ganhar o negócio — abre a venda'
            : `Mover para ${rotulo}`;
          return (
            <Tooltip key={etapa} title={dica}>
              <button type="button" className={classe} aria-current={atual ? 'step' : undefined}
                disabled={atual || ganho || !acao}
                onClick={() => acao?.()}>
                <span className="te-nome">
                  {!compacta && (feito || (ganho && i < STATUS_ETAPAS.length - 1)) && <CheckOutlined style={{ fontSize: 10, marginRight: 4 }} />}
                  {compacta ? (ROTULO_CURTO[etapa] || rotulo) : rotulo}
                </span>
                {atual && tempo && !compacta && <span className="te-tempo">{tempo}</span>}
              </button>
            </Tooltip>
          );
        })}
      </div>
    </>
  );
}
